import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Document, Page, pdfjs } from 'react-pdf';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import LanguageSwitcher from './LanguageSwitcher';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFFile {
  id: string;
  name: string;
  originalName?: string;
  encrypted?: boolean;
  size: number;
  uploadedAt: string;
  url: string;
  aiAnalysis?: string;
  extractedData?: {
    invoiceNumber?: string;
    date?: string;
    amount?: number;
    vendor?: string;
    currency?: string;
    taxAmount?: number;
    totalAmount?: number;
    items?: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
      total: number;
    }>;
  };
}

interface AnalysisResponse {
  analysis: string;
  message: string;
  analysisFiles?: {
    xml: {
      id: string;
      type: string;
    };
    excel: {
      id: string;
      type: string;
    };
  };
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [files, setFiles] = useState<PDFFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [viewingPdf, setViewingPdf] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [analyzingFile, setAnalyzingFile] = useState<string | null>(null);
  const [analysisResults, setAnalysisResults] = useState<{[key: string]: string}>({});
  const [analysisFiles, setAnalysisFiles] = useState<{[key: string]: {xml: {id: string, type: string}, excel: {id: string, type: string}}}>({});
  const [exporting, setExporting] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [senderEmail, setSenderEmail] = useState('');
  const [processingConsent, setProcessingConsent] = useState(false);

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    if (user?.email) {
      setSenderEmail(user.email);
    }
  }, [user]);

  const fetchFiles = async () => {
    try {
      const response = await axios.get<PDFFile[]>(API_ENDPOINTS.files.list);
      setFiles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      setFiles([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      alert(t('dashboard.pdfOnly'));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    if (!senderEmail) {
      alert('Sender email is required');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('senderEmail', senderEmail);
    formData.append('processingConsent', String(processingConsent));

    try {
      await axios.post(API_ENDPOINTS.files.upload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSelectedFile(null);
      fetchFiles();
    } catch (error) {
      console.error('Upload failed:', error);
      alert(t('dashboard.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async (fileId: string) => {
    setAnalyzingFile(fileId);
    try {
      const response = await axios.post<AnalysisResponse>(API_ENDPOINTS.files.analyze(fileId), {});
      setAnalysisResults(prev => ({
        ...prev,
        [fileId]: response.data.analysis
      }));
      
      // Store analysis files if available
      if (response.data.analysisFiles) {
        setAnalysisFiles(prev => ({
          ...prev,
          [fileId]: response.data.analysisFiles!
        }));
      }
    } catch (error: any) {
      console.error('Analysis failed:', error);
      alert(error.response?.data?.error || t('dashboard.analysisError'));
    } finally {
      setAnalyzingFile(null);
    }
  };

  const handleViewPdf = (file: PDFFile) => {
    const fileUrl = API_ENDPOINTS.files.content(file.id);
    console.log('Loading PDF:', fileUrl);
    setPdfLoading(true);
    setViewingPdf(fileUrl);
    setPageNumber(1);
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await axios.delete(API_ENDPOINTS.files.delete(fileId));
      fetchFiles();
    } catch (error) {
      console.error('Delete failed:', error);
      alert(t('errors.generic'));
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    alert('Failed to load PDF file. Please try again.');
    setViewingPdf(null);
    setPdfLoading(false);
  };

  const handleExport = async (format: string) => {
    if (files.length === 0) {
      alert('No files to export');
      return;
    }

    setExporting(true);
    try {
      const response = await axios.get(API_ENDPOINTS.files.export(format), {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename based on format
      const filename = `invoices_export.${format}`;
      link.setAttribute('download', filename);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Export failed:', error);
      alert(error.response?.data?.error || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const downloadFile = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAnalysisFile = async (fileId: string) => {
    try {
      // Get auth token from localStorage or context
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required');
        return;
      }

      const response = await axios.get<Blob>(`${API_ENDPOINTS.files.list}/analysis-file/${fileId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1] || 'analysis-file'
        : 'analysis-file';
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download failed:', error);
      alert('Failed to download file');
    }
  };

  const toggleFileExpansion = (fileId: string) => {
    const newExpandedFiles = new Set(expandedFiles);
    if (newExpandedFiles.has(fileId)) {
      newExpandedFiles.delete(fileId);
    } else {
      newExpandedFiles.add(fileId);
    }
    setExpandedFiles(newExpandedFiles);
  };

  if (viewingPdf) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">PDF Viewer</h2>
              <button
                onClick={() => setViewingPdf(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Back to Dashboard
              </button>
            </div>
            
            <div className="flex justify-center mb-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                  disabled={pageNumber <= 1}
                  className="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-gray-700">
                  Page {pageNumber} of {numPages}
                </span>
                <button
                  onClick={() => setPageNumber(Math.min(numPages || 1, pageNumber + 1))}
                  disabled={pageNumber >= (numPages || 1)}
                  className="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex justify-center">
              {pdfLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-gray-600">Loading PDF...</div>
                </div>
              ) : (
                <Document
                  file={viewingPdf}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  className="border border-gray-300"
                >
                  <Page pageNumber={pageNumber} width={800} />
                </Document>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <h1 className="text-xl font-semibold text-slate-800">Alenis</h1>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              <span className="text-slate-600">{t('dashboard.welcome', { name: user?.name })}</span>
              <a
                href="/profile"
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              >
                {t('navigation.profile')}
              </a>
              <button
                onClick={logout}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                {t('navigation.logout')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-8 px-6">
        {/* Upload Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">{t('dashboard.uploadPdf')}</h2>
          <div className="flex items-center space-x-4">
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors"
            />
            <input
              type="email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              className="w-72 px-3 py-2 border border-slate-300 rounded-lg text-sm"
              placeholder="Sender email"
            />
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {uploading ? t('dashboard.uploading') : t('common.upload')}
            </button>
          </div>
          <div className="mt-3 flex items-center space-x-2">
            <input
              id="processing-consent-auth"
              type="checkbox"
              checked={processingConsent}
              onChange={(e) => setProcessingConsent(e.target.checked)}
            />
            <label htmlFor="processing-consent-auth" className="text-sm text-slate-600">
              I allow Alenis to process this file and my sender email for further company processing.
            </label>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {t('dashboard.uploadLiabilityNotice')}
          </p>
          {selectedFile && (
            <p className="mt-3 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
              Wybrany plik: {selectedFile.name}
            </p>
          )}
        </div>

        {/* Files List */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-800">Twoje dokumenty PDF</h2>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-slate-500">
                {!files || files.length === 0 ? '' : `${files.length} ${files.length === 1 ? 'dokument' : files.length < 5 ? 'dokumenty' : 'dokumentów'}`}
              </div>
              {files && files.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-slate-600">Export:</span>
                  <button
                    onClick={() => handleExport('csv')}
                    disabled={exporting}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {exporting ? 'Exporting...' : 'CSV'}
                  </button>
                  <button
                    onClick={() => handleExport('json')}
                    disabled={exporting}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {exporting ? 'Exporting...' : 'JSON'}
                  </button>
                  <button
                    onClick={() => handleExport('xml')}
                    disabled={exporting}
                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {exporting ? 'Exporting...' : 'XML'}
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-500">Ładowanie...</p>
            </div>
          ) : !files || files.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="w-12 h-12 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-slate-500">{t('dashboard.noFiles')}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {files && files.map((file) => (
                <div key={file.id} className="p-6 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-slate-800">{file.originalName || file.name}</h3>
                            <p className="text-sm text-slate-500">
                              {formatFileSize(file.size)} • {t('dashboard.uploadDate')}: {new Date(file.uploadedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => toggleFileExpansion(file.id)}
                            className="ml-4 p-2 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <svg 
                              className={`w-5 h-5 transform transition-transform ${expandedFiles.has(file.id) ? 'rotate-180' : ''}`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        {analysisResults[file.id] && (
                          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                            <strong>{t('dashboard.aiAnalysis')}:</strong> {analysisResults[file.id]}
                            {analysisFiles[file.id] && (
                              <div className="mt-2 flex space-x-2">
                                <button
                                  onClick={() => downloadAnalysisFile(analysisFiles[file.id].xml.id)}
                                  className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                                >
                                  📄 Download XML
                                </button>
                                <button
                                  onClick={() => downloadAnalysisFile(analysisFiles[file.id].excel.id)}
                                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                >
                                  📊 Download Excel
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewPdf(file)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        {t('dashboard.viewFile')}
                      </button>
                      <button
                        onClick={() => handleAnalyze(file.id)}
                        disabled={analyzingFile === file.id}
                        className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {analyzingFile === file.id ? t('dashboard.analyzing') : t('dashboard.analyzeWithAI')}
                      </button>
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className="px-4 py-2 bg-slate-100 text-slate-600 text-sm rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        {t('dashboard.deleteFile')}
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedFiles.has(file.id) && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* File Information */}
                        <div className="bg-slate-50 rounded-lg p-4">
                          <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
                            <svg className="w-4 h-4 mr-2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Informacje o pliku
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-600">Nazwa pliku:</span>
                              <span className="font-medium">{file.originalName || file.name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Rozmiar:</span>
                              <span className="font-medium">{formatFileSize(file.size)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Data przesłania:</span>
                              <span className="font-medium">{new Date(file.uploadedAt).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">ID pliku:</span>
                              <span className="font-medium text-xs">{file.id}</span>
                            </div>
                          </div>
                        </div>

                        {/* Extracted Data */}
                        {file.extractedData && (
                          <div className="bg-green-50 rounded-lg p-4">
                            <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
                              <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Wyekstraktowane dane
                            </h4>
                            <div className="space-y-2 text-sm">
                              {file.extractedData.invoiceNumber && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Numer faktury:</span>
                                  <span className="font-medium">{file.extractedData.invoiceNumber}</span>
                                </div>
                              )}
                              {file.extractedData.date && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Data:</span>
                                  <span className="font-medium">{file.extractedData.date}</span>
                                </div>
                              )}
                              {file.extractedData.vendor && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Sprzedawca:</span>
                                  <span className="font-medium">{file.extractedData.vendor}</span>
                                </div>
                              )}
                              {file.extractedData.totalAmount && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Kwota całkowita:</span>
                                  <span className="font-medium">{file.extractedData.totalAmount} {file.extractedData.currency || 'PLN'}</span>
                                </div>
                              )}
                              {file.extractedData.taxAmount && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Kwota podatku:</span>
                                  <span className="font-medium">{file.extractedData.taxAmount} {file.extractedData.currency || 'PLN'}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Items List */}
                        {file.extractedData?.items && file.extractedData.items.length > 0 && (
                          <div className="md:col-span-2 bg-blue-50 rounded-lg p-4">
                            <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
                              <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                              Pozycje faktury
                            </h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-blue-200">
                                    <th className="text-left py-2 text-slate-600">Opis</th>
                                    <th className="text-right py-2 text-slate-600">Ilość</th>
                                    <th className="text-right py-2 text-slate-600">Cena jednostkowa</th>
                                    <th className="text-right py-2 text-slate-600">Razem</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {file.extractedData.items.map((item, index) => (
                                    <tr key={index} className="border-b border-blue-100">
                                      <td className="py-2 font-medium">{item.description}</td>
                                      <td className="py-2 text-right">{item.quantity}</td>
                                      <td className="py-2 text-right">{item.unitPrice} {file.extractedData?.currency || 'PLN'}</td>
                                      <td className="py-2 text-right font-medium">{item.total} {file.extractedData?.currency || 'PLN'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* AI Analysis */}
                        {analysisResults[file.id] && (
                          <div className="md:col-span-2 bg-purple-50 rounded-lg p-4">
                            <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
                              <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                              Analiza AI
                            </h4>
                            <p className="text-sm text-slate-700">{analysisResults[file.id]}</p>
                            {analysisFiles[file.id] && (
                              <div className="mt-3 flex space-x-2">
                                <button
                                  onClick={() => downloadAnalysisFile(analysisFiles[file.id].xml.id)}
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Pobierz XML
                                </button>
                                <button
                                  onClick={() => downloadAnalysisFile(analysisFiles[file.id].excel.id)}
                                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors flex items-center"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Pobierz Excel
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 